import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './DynamicCMSPage.css';

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="#10b981"/>
    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#1285ff"/>
    <path d="M10 8L16 12L10 16V8Z" fill="white"/>
  </svg>
);

const HelpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="#1285ff" strokeWidth="2"/>
    <path d="M12 17V17.01M12 14C12 11 15 11 15 9C15 7.34 13.66 6 12 6C10.34 6 9 7.34 9 9" stroke="#1285ff" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

function DynamicCMSPage() {
  const [selectedPlan, setSelectedPlan] = useState('Architect');
  const [cachingServer, setCachingServer] = useState(false);
  const [billingYearly, setBillingYearly] = useState(false);

  const plans = [
    { name: 'Starter', price: 49 },
    { name: 'Builder', price: 99 },
    { name: 'Architect', price: 149 },
  ];

  // Features before the rowspan (rows 1-5: these don't show Visionary values)
  const featuresBeforeRowspan = [
    { name: 'Custom objects included', values: ['2', '5', '8'] },
    { name: 'Additional custom objects', values: ['$20/month', '$20/month', '$20/month'] },
    { name: '*Records stored per custom object', values: ['2,000', '10,000', '25,000'] },
    { name: '*Total dynamic records displayed/month', values: ['10,000', '50,000', '200,000'] },
    { name: 'Rollup fields', values: ['50', '100', '200'] },
  ];

  // Features after the rowspan (these show checkmarks for Visionary)
  const featuresAfterRowspan = [
    { name: 'High-performance caching server', values: ['$49/month', '$99/month', '$149/month', 'check'] },
    { name: 'Custom objects', values: ['check', 'check', 'check', 'check'] },
    { name: 'App templates for blogs, forums, courses and more', values: ['check', 'check', 'check', 'check'] },
    { name: 'Database-driven page templates', values: ['check', 'check', 'check', 'check'] },
    { name: 'Dynamic page hosting', values: ['check', 'check', 'check', 'check'] },
    { name: 'Dynamic blocks', values: ['check', 'check', 'check', 'check'] },
    { name: 'Grid layout', values: ['check', 'check', 'check', 'check'] },
    { name: 'Search', values: ['check', 'check', 'check', 'check'] },
    { name: 'Rollup fields', values: ['check', 'check', 'check', 'check'] },
    { name: 'Filter and sort', values: ['dash', 'check', 'check', 'check'] },
    { name: 'Dynamic list HTML tags', values: ['dash', 'dash', 'check', 'check'] },
  ];

  const selectedPlanPrice = plans.find(p => p.name === selectedPlan)?.price || 149;

  return (
    <div className="dynamic-cms-page">
      {/* Header */}
      <header className="cms-header">
        <Link to="/" className="close-button">
          <CloseIcon />
        </Link>
        <h1 className="header-title">Change plan</h1>
      </header>

      {/* Main Content */}
      <main className="cms-main">
        <div className="cms-content">
          {/* Left Section - Plans Table */}
          <div className="cms-plans-section">
            <h2 className="cms-title">Dynamic CMS</h2>
            <p className="cms-description">
              Display unique, personalized content for your contacts. Learn more about Dynamic CMS <a href="#" className="link">here</a> and <a href="#" className="link">here</a>.
            </p>
            <p className="cms-overage">*Overage fees may apply</p>
            
            <a href="#" className="watch-video">
              <PlayIcon />
              <span>Watch the video</span>
            </a>

            <div className="plans-table-container">
              <table className="plans-table">
                <thead>
                  <tr>
                    <th className="feature-column"></th>
                    {plans.map((plan) => (
                      <th key={plan.name} className="plan-column">
                        <div className="plan-header-content">
                          <div className="plan-name">{plan.name}</div>
                          <div className="plan-price">${plan.price}/month</div>
                          <button 
                            className="add-toggle-btn"
                            onClick={() => setSelectedPlan(selectedPlan === plan.name ? null : plan.name)}
                          >
                            <span>{selectedPlan === plan.name ? 'Added' : 'Add'}</span>
                            <span className={`toggle-track ${selectedPlan === plan.name ? 'active' : ''}`}></span>
                          </button>
                        </div>
                      </th>
                    ))}
                    <th className="plan-column visionary-header">
                      <div className="plan-header-content">
                        <div className="plan-name">Visionary</div>
                        <div className="plan-price">Custom pricing</div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* First row with rowspan for Visionary */}
                  <tr>
                    <td className="feature-name">{featuresBeforeRowspan[0].name}</td>
                    {featuresBeforeRowspan[0].values.map((value, vIdx) => (
                      <td key={vIdx} className="feature-value">
                        <span className="feature-text">{value}</span>
                      </td>
                    ))}
                    <td className="feature-value visionary-contact" rowSpan={featuresBeforeRowspan.length}>
                      <div className="visionary-contact-text">
                        <a href="#" className="link">Contact us</a> for custom pricing up to 5 million records and unlimited views.
                      </div>
                    </td>
                  </tr>
                  {/* Remaining rows before rowspan (no Visionary cell) */}
                  {featuresBeforeRowspan.slice(1).map((feature, idx) => (
                    <tr key={idx}>
                      <td className="feature-name">{feature.name}</td>
                      {feature.values.map((value, vIdx) => (
                        <td key={vIdx} className="feature-value">
                          <span className="feature-text">{value}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Features after rowspan (with Visionary checkmarks) */}
                  {featuresAfterRowspan.map((feature, idx) => (
                    <tr key={idx}>
                      <td className="feature-name">{feature.name}</td>
                      {feature.values.slice(0, 3).map((value, vIdx) => (
                        <td key={vIdx} className="feature-value">
                          {value === 'check' ? (
                            <CheckIcon />
                          ) : value === 'dash' ? (
                            <span className="dash">—</span>
                          ) : (
                            <span className="feature-text">{value}</span>
                          )}
                        </td>
                      ))}
                      <td className="feature-value">
                        {feature.values[3] === 'check' ? (
                          <CheckIcon />
                        ) : feature.values[3] === 'dash' ? (
                          <span className="dash">—</span>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Section - Sidebar */}
          <aside className="cms-sidebar">
            <div className="sidebar-card">
              {/* High-performance Caching Server */}
              <div className="sidebar-option">
                <div className="option-label-row">
                  <span className="option-label">High-performance<br/>Caching Server</span>
                  <HelpIcon />
                </div>
                <button 
                  className={`option-toggle-btn`}
                  onClick={() => setCachingServer(!cachingServer)}
                >
                  <span className={`toggle-track ${cachingServer ? 'active' : ''}`}></span>
                </button>
              </div>

              {/* Billed yearly */}
              <div className="sidebar-option">
                <div className="option-label-col">
                  <span className="option-label">Billed yearly</span>
                  <span className="option-sublabel">Get 2 months free</span>
                </div>
                <button 
                  className={`option-toggle-btn`}
                  onClick={() => setBillingYearly(!billingYearly)}
                >
                  <span className={`toggle-track ${billingYearly ? 'active' : ''}`}></span>
                </button>
              </div>

              {/* Price Display */}
              <div className="price-display">
                <div className="price-due">
                  <span className="price-currency">$</span>
                  <span className="price-amount">57</span>
                  <span className="price-due-text">due in 14 days</span>
                </div>
                <a href="#" className="partial-month-link">Partial month of Dynamic CMS</a>
                <div className="price-then">then</div>
                <div className="price-monthly">
                  <span className="price-currency">$</span>
                  <span className="price-amount">{selectedPlanPrice}</span>
                  <span className="price-period">per month</span>
                </div>
              </div>

              <button className="start-trial-btn">Start trial</button>

              <p className="tax-note">Subject to local sales tax rates based on your billing address.</p>

              <a href="#" className="cancel-link">Cancel my Dynamic CMS plan</a>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default DynamicCMSPage;
